import React, { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Download, Lock, ArrowRight } from 'lucide-react';
import { getNcrById, type Ncr } from '../../services/ncrService.ts';
import './NcrDetail.css';

const NcrDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notify = useNotify();

  const [ncr, setNcr] = useState<Ncr | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedStages, setExpandedStages] = useState<Set<number>>(new Set());

  const formatStatusLabel = (value?: number | string) => {
    if (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value))) {
      const n = Number(value);
      if (n === 1) return 'In Progress';
      if (n === 2) return 'Completed';
      if (n === 3) return 'Blocked';
      return 'Pending';
    }
    const normalized = (value || '').toLowerCase();
    if (normalized === 'in-progress' || normalized === 'in progress') return 'In Progress';
    if (normalized === 'close' || normalized === 'closed' || normalized === 'completed') return 'Completed';
    return 'Pending';
  };

  const normalizeStatus = (value?: number | string) => {
    if (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value))) {
      const n = Number(value);
      if (n === 1) return 'in-progress';
      if (n === 2) return 'close';
      if (n === 3) return 'blocked';
      return 'open';
    }
    const normalized = (value || '').toLowerCase();
    if (normalized === 'draft' || normalized === 'open' || normalized === 'pending') return 'open';
    if (normalized === 'completed' || normalized === 'closed' || normalized === 'close') return 'close';
    if (normalized === 'in progress') return 'in-progress';
    return normalized === 'in-progress' ? 'in-progress' : 'open';
  };

  const formatDateOnly = (value?: string) => (value ? value.slice(0, 10) : '—');

  useEffect(() => {
    const fetchNcr = async () => {
      if (!id) return;
      try {
        const data = await getNcrById(Number(id));
        setNcr(data);
        if (data.stages && data.stages.length > 0) {
          setExpandedStages(new Set([0]));
        }
      } catch (error: any) {
        notify(error.message || 'Failed to load NCR', { type: 'warning' });
        navigate('/Ncr');
      } finally {
        setLoading(false);
      }
    };
    fetchNcr();
  }, [id, navigate, notify]);

  const isStageLocked = (stageIndex: number): boolean => {
    if (!ncr?.stageDependencies) return false;
    const deps = ncr.stageDependencies.filter(
      (dep) => dep.toStageId === ncr.stages?.[stageIndex]?.id && dep.dependencyType === 'sequential'
    );
    return deps.some((dep) => {
      const from = ncr.stages?.find((s) => s.id === dep.fromStageId);
      return from && normalizeStatus(from.status) !== 'close';
    });
  };

  const toggleStage = (index: number) => {
    const next = new Set(expandedStages);
    next.has(index) ? next.delete(index) : next.add(index);
    setExpandedStages(next);
  };

  if (loading) return <div className="vw-loading">Loading NCR details…</div>;
  if (!ncr) return <div className="vw-error">NCR not found</div>;

  const ncrStatus = normalizeStatus(ncr.status);

  return (
    <div className="vw-container">
      {/* ── Header ── */}
      <div className="vw-header">
        <div className="vw-header-left">
          <p className="vw-header-eyebrow">NCR Detail</p>
          <h1 className="vw-header-title">{ncr.ncrNumber}</h1>
          <div className="vw-header-meta">
            <span className="vw-template-tag">{ncr.templateName}</span>
            <span className={`vw-status-pill vw-status-${ncrStatus}`}>{formatStatusLabel(ncrStatus)}</span>
          </div>
        </div>
        <button className="vw-back-btn" onClick={() => navigate('/Ncr')}>
          ← Back to List
        </button>
      </div>

      <div className="vw-body">
        {/* ── Overview ── */}
        <section className="vw-card">
          <h2 className="vw-card-title">Overview</h2>
          <div className="vw-overview-grid">
            {ncr.description && (
              <div className="vw-field vw-field--full">
                <span className="vw-field-label">Description</span>
                <span className="vw-field-value vw-field-value--text">{ncr.description}</span>
              </div>
            )}
            <div className="vw-field">
              <span className="vw-field-label">Raised Date</span>
              <span className="vw-field-value">{formatDateOnly(ncr.raisedDate)}</span>
            </div>
            <div className="vw-field">
              <span className="vw-field-label">Target Date</span>
              <span className="vw-field-value">{formatDateOnly(ncr.targetDate)}</span>
            </div>
            <div className="vw-field">
              <span className="vw-field-label">Severity</span>
              <span className="vw-field-value">{ncr.severity || '—'}</span>
            </div>
            <div className="vw-field">
              <span className="vw-field-label">Owner</span>
              <span className="vw-field-value">{ncr.ownerName || '—'}</span>
            </div>
            <div className="vw-field">
              <span className="vw-field-label">Created</span>
              <span className="vw-field-value">
                {ncr.createdAt ? new Date(ncr.createdAt).toLocaleDateString('en-GB') : '—'}
              </span>
            </div>
            <div className="vw-field">
              <span className="vw-field-label">Last Updated</span>
              <span className="vw-field-value">
                {ncr.updatedAt ? new Date(ncr.updatedAt).toLocaleDateString('en-GB') : '—'}
              </span>
            </div>
          </div>
        </section>

        {/* ── Workflow Stages ── */}
        <section className="vw-card">
          <h2 className="vw-card-title">Workflow Stages</h2>
          <div className="vw-stages">
            {ncr.stages.map((stage, si) => {
              const locked = isStageLocked(si);
              const expanded = expandedStages.has(si);
              const stageStatus = normalizeStatus(stage.status);
              return (
                <div key={si} className={`vw-stage${locked ? ' vw-stage--locked' : ''}`}>
                  {/* Stage header bar */}
                  <button
                    className="vw-stage-bar"
                    onClick={() => toggleStage(si)}
                    aria-expanded={expanded}
                  >
                    <div className="vw-stage-bar-left">
                      <span className="vw-stage-chevron">
                        {expanded ? <ChevronUp size={17} /> : <ChevronDown size={17} />}
                      </span>
                      <span className="vw-stage-index">Stage {si + 1}</span>
                      <span className="vw-stage-name">{stage.stageName}</span>
                      {locked && (
                        <span className="vw-locked-tag">
                          <Lock size={12} /> Locked
                        </span>
                      )}
                    </div>
                    <span className={`vw-status-pill vw-status-${stageStatus}`}>
                      {formatStatusLabel(stage.status)}
                    </span>
                  </button>

                  {/* Stage body */}
                  {expanded && (
                    <div className="vw-stage-body">
                      {/* Stage Info */}
                      <div className="vw-stage-section">
                        <h4 className="vw-stage-section-title">Stage Details</h4>
                        <div className="vw-action-fields">
                          <div className="vw-action-field">
                            <span className="vw-field-label">Owner</span>
                            <span className="vw-field-value">{stage.ownerName || '—'}</span>
                          </div>
                          <div className="vw-action-field">
                            <span className="vw-field-label">Level</span>
                            <span className="vw-field-value">{stage.workflowLevel ?? '—'}</span>
                          </div>
                          <div className="vw-action-field">
                            <span className="vw-field-label">Target Date</span>
                            <span className="vw-field-value">{formatDateOnly(stage.targetDate)}</span>
                          </div>
                          <div className="vw-action-field">
                            <span className="vw-field-label">Created Date</span>
                            <span className="vw-field-value">{formatDateOnly(stage.createdAt)}</span>
                          </div>
                          <div className="vw-action-field">
                            <span className="vw-field-label">Complete Date</span>
                            <span className="vw-field-value">{formatDateOnly(stage.completeDate)}</span>
                          </div>
                          {stage.detail && (
                            <div className="vw-action-field vw-action-field--full">
                              <span className="vw-field-label">Description</span>
                              <span className="vw-field-value vw-field-value--text">{stage.detail}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Properties */}
                      {stage.properties && stage.properties.length > 0 && (
                        <div className="vw-stage-section">
                          <h4 className="vw-stage-section-title">Properties</h4>
                          <div className="vw-props-grid">
                            {stage.properties.map((prop, pi) => (
                              <div key={pi} className="vw-prop-card">
                                <span className="vw-field-label">{prop.propertyName}</span>
                                <span className="vw-prop-value">{prop.value || '—'}</span>
                                {prop.attachments && prop.attachments.length > 0 && (
                                  <div className="vw-prop-files">
                                    {prop.attachments.map((att) => (
                                      <a
                                        key={att.id}
                                        href={att.filePath}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="vw-file-chip"
                                      >
                                        <Download size={12} />
                                        {att.fileName}
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="vw-stage-section">
                        <h4 className="vw-stage-section-title">
                          Actions
                          <span className="vw-action-badge">{stage.actions?.length || 0}</span>
                        </h4>
                        {stage.actions && stage.actions.length > 0 ? (
                          <div className="vw-actions-list">
                            {stage.actions.map((action, ai) => {
                              const actStatus = normalizeStatus(action.status);
                              return (
                                <div key={ai} className="vw-action-card">
                                  <div className="vw-action-card-header">
                                    <span className="vw-action-title">
                                      Action {action.actionNumber || ai + 1}
                                    </span>
                                    <span className={`vw-status-pill vw-status-${actStatus}`}>
                                      {formatStatusLabel(action.status)}
                                    </span>
                                  </div>
                                  <div className="vw-action-fields">
                                    <div className="vw-action-field">
                                      <span className="vw-field-label">Owner</span>
                                      <span className="vw-field-value">{action.ownerName || action.ownerId || '—'}</span>
                                    </div>
                                    <div className="vw-action-field">
                                      <span className="vw-field-label">Created Date</span>
                                      <span className="vw-field-value">{formatDateOnly(action.createdAt)}</span>
                                    </div>
                                    <div className="vw-action-field">
                                      <span className="vw-field-label">Target Date</span>
                                      <span className="vw-field-value">{formatDateOnly(action.targetDate)}</span>
                                    </div>
                                    <div className="vw-action-field">
                                      <span className="vw-field-label">Complete Date</span>
                                      <span className="vw-field-value">{formatDateOnly(action.completeDate)}</span>
                                    </div>
                                    {action.actionDetails && (
                                      <div className="vw-action-field vw-action-field--full">
                                        <span className="vw-field-label">Action Details</span>
                                        <span className="vw-field-value vw-field-value--text">
                                          {action.actionDetails}
                                        </span>
                                      </div>
                                    )}
                                    {action.actionResponse && (
                                      <div className="vw-action-field vw-action-field--full">
                                        <span className="vw-field-label">Action Response</span>
                                        <span className="vw-field-value vw-field-value--text">
                                          {action.actionResponse}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  {action.attachments && action.attachments.length > 0 && (
                                    <div className="vw-action-files">
                                      <span className="vw-field-label">Attachments</span>
                                      <div className="vw-file-chips">
                                        {action.attachments.map((att) => (
                                          <a
                                            key={att.id}
                                            href={att.filePath}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="vw-file-chip"
                                          >
                                            <Download size={12} />
                                            {att.fileName}
                                          </a>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="vw-empty">No actions recorded for this stage.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Dependencies ── */}
        {ncr.stageDependencies && ncr.stageDependencies.length > 0 && (
          <section className="vw-card">
            <h2 className="vw-card-title">Stage Dependencies</h2>
            <div className="vw-deps-list">
              {ncr.stageDependencies.map((dep, i) => {
                const from = ncr.stages.find((s) => s.id === dep.fromStageId);
                const to = ncr.stages.find((s) => s.id === dep.toStageId);
                return (
                  <div key={i} className="vw-dep-row">
                    <span className="vw-dep-stage">{from?.stageName || '—'}</span>
                    <ArrowRight size={16} className="vw-dep-arrow" />
                    <span className="vw-dep-stage">{to?.stageName || '—'}</span>
                    <span className="vw-dep-type">{dep.dependencyType}</span>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default NcrDetail;
